import { LensTheme } from '@diligentcorp/atlas-theme-mui-lens';
import { Tab, Tabs } from '@mui/material';
import { Box, Stack, useTheme } from '@mui/system';
import { useEffect, useState } from 'react';
import { Redirect } from 'react-router-dom';
import { CaseAuditLog } from 'src/components/atlas/organisms/CaseAuditLog';
import { Center, Spinner } from 'src/components/atoms';
import { handleErrorToast } from 'src/components/molecules';
import { CaseFormResponses } from 'src/components/organisms/CaseFormResponses/CaseFormResponses';
import { CaseHeader } from 'src/components/organisms/CaseHeader/CaseHeader';
import { CaseOverviewPanel } from 'src/components/organisms/CaseOverviewPanel/CaseOverviewPanel';
import { CaseSubjectDetails } from 'src/components/organisms/CaseSubjectDetails/CaseSubjectDetails';
import {
  CategoryDisplayType,
  DisclosureResponseType,
  useDisclosureResponseByIdQuery,
} from 'src/graphql';
import { useQueryParams } from 'src/hooks';
import theme from 'src/theme/theme';
import { CaseTypeEnum } from 'src/types';

export const AutoClosedDisclosureDetail = () => {
  const queryParams = useQueryParams();
  const responseId = queryParams.get('id') ?? '';

  const { tokens, presets } = useTheme<LensTheme>();
  const { TabsPresets } = presets;

  const { data, isLoading, isError } = useDisclosureResponseByIdQuery(
    { responseId },
    { enabled: Boolean(responseId) },
  );

  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!responseId) {
      handleErrorToast({ toastMessage: 'Missing disclosure id' });
    }
    if (isError) {
      handleErrorToast({ toastMessage: 'Invalid disclosure id' });
    }
  }, [responseId, isError]);

  if (!responseId) {
    return <Redirect to="/disclosures" />;
  }

  if (isError) {
    return <Redirect to="/disclosures" />;
  }

  if (isLoading) {
    return (
      <Center data-testid="auto-closed-disclosure-detail-loading">
        <Spinner variant="secondary" size="xlarge" />
      </Center>
    );
  }

  const responseInfo = data?.disclosureResponseById;
  const { reporter, respondent } = responseInfo ?? {};
  const reporterForDetails = {
    displayName: reporter?.name ?? respondent?.name,
    name: reporter?.name ?? respondent?.name,
    email: reporter?.contactEmail ?? respondent?.email,
    eid: reporter?.eid,
    role: reporter?.role,
    department: reporter?.department,
    site: reporter?.site,
    country: reporter?.country,
  };

  return (
    <Box
      sx={{
        padding: `${tokens.core.spacing[3].value} ${tokens.core.spacing[6].value}`,
      }}
    >
      <CaseHeader
        caseStatus={responseInfo?.status?.replaceAll('_', ' ') ?? ''}
        openMessages={undefined}
        reportId={undefined}
        view={'limited'}
        caseType={CaseTypeEnum.COI}
      />
      <Stack
        sx={{
          marginTop: tokens.core.spacing[1].value,
        }}
      >
        <Tabs
          aria-label="case tabs"
          onChange={(_, newVal) => setValue(newVal)}
          value={value}
          {...TabsPresets.Tabs.alignToPageHeader}
          sx={{
            alignSelf: 'stretch',
            borderBottom: `1px solid ${theme.atlas.semantic.color.ui.divider.default}`,
          }}
        >
          <Tab label="Report" id="tab-0" aria-controls="tabpanel-0" />
          <Tab label="Audit log" id="tab-1" aria-controls="tabpanel-1" />
        </Tabs>
        <div
          role="tabpanel"
          hidden={value !== 0}
          id="tabpanel-0"
          aria-labelledby="tab-0"
        >
          {value === 0 && (
            <>
              <Box
                sx={{
                  marginTop: tokens.core.spacing[4].value,
                  backgroundColor: theme.atlas.semantic.color.surface.variant,
                  borderRadius: tokens.semantic.radius.md.value,
                  padding: 3,
                  display: 'flex',
                  gap: '24px',
                }}
              >
                <CaseOverviewPanel
                  label={responseInfo?.questionnaire?.publicTitle ?? ''}
                  caseLead={null}
                  categories={[
                    {
                      name: responseInfo?.questionnaire?.publicTitle ?? '',
                    } as CategoryDisplayType,
                  ]}
                  status={responseInfo?.status?.replaceAll('_', ' ') ?? ''}
                  submittedAt={
                    responseInfo?.submittedAt
                      ? new Date(responseInfo.submittedAt)
                      : new Date()
                  }
                  lastUpdated={
                    responseInfo?.updatedAt
                      ? new Date(responseInfo.updatedAt)
                      : null
                  }
                  caseType={CaseTypeEnum.COI}
                  view="limited"
                />
                <CaseSubjectDetails
                  reporter={reporterForDetails}
                  caseType={CaseTypeEnum.COI}
                />
              </Box>
              <Box
                sx={{
                  marginTop: tokens.core.spacing[3].value,
                  backgroundColor: theme.atlas.semantic.color.surface.variant,
                  borderRadius: tokens.semantic.radius.md.value,
                  padding: '24px',
                }}
              >
                <CaseFormResponses
                  responseInfo={responseInfo as DisclosureResponseType}
                  caseType={CaseTypeEnum.COI}
                />
              </Box>
            </>
          )}
        </div>
        <div
          role="tabpanel"
          hidden={value !== 1}
          id="tabpanel-1"
          aria-labelledby="tab-1"
        >
          {value === 1 && (
            <Box sx={{ padding: tokens.core.spacing[4].value }}>
              <CaseAuditLog responseId={responseId} />
            </Box>
          )}
        </div>
      </Stack>
    </Box>
  );
};
